import { loadAllProfiles } from './data/marketplaces';
import type { CalculatorInput, MarketplaceCode } from './engine/types';
import { Store } from './state/store';
import { getDomRefs, setHidden } from './ui/dom';
import { attachEvents } from './ui/events';
import { render } from './ui/render';

type BootContext = 'standalone' | 'wordpress';

export interface BootConfig {
  marketplace?: MarketplaceCode;
  profileId?: string;
  context?: BootContext;
  showHeader?: boolean;
  assetBaseUrl?: string;
}

const SHELL_CACHE = new Map<string, string>();

function defaultInput(): CalculatorInput {
  return {
    category: '',
    price: 0,
    cogs: 0,
    weight: 0,
    length: 0,
    width: 0,
    height: 0,
    inbound: 0,
    duties: 0,
    prep: 0,
    inboundPlacementMode: 'optimized',
    inboundPlacementManual: 0
  };
}

function normalizeBaseUrl(value: string): string {
  return value.endsWith('/') ? value : `${value}/`;
}

function resolveAssetBaseUrl(config?: BootConfig): string {
  if (config?.assetBaseUrl) {
    return normalizeBaseUrl(config.assetBaseUrl);
  }

    const win = window as Window & { APC_V3_ASSET_BASE_URL?: string };
  if (win.APC_V3_ASSET_BASE_URL) {
    return normalizeBaseUrl(win.APC_V3_ASSET_BASE_URL);
  }

  return '/';
}

async function loadShellMarkup(baseUrl: string): Promise<string> {
  const normalizedBaseUrl = normalizeBaseUrl(baseUrl);
  const cached = SHELL_CACHE.get(normalizedBaseUrl);
  if (cached) {
    return cached;
  }

  const sourceUrl = new URL('index.html', normalizedBaseUrl).toString();
  const response = await fetch(sourceUrl, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Failed to load calculator shell from ${sourceUrl}`);
  }

  const html = await response.text();
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const shell = parsed.getElementById('apc-v3-shell');
  if (!shell) {
    throw new Error('Calculator shell is missing #apc-v3-shell in index.html');
  }

  const markup = `<div class="apc-shell-wrap w-full max-w-3xl mx-auto">${shell.outerHTML}</div>`;
  SHELL_CACHE.set(normalizedBaseUrl, markup);
  return markup;
}

async function ensureContainerMarkup(container: HTMLElement, context: BootContext, assetBaseUrl: string): Promise<void> {
  if (container.querySelector('#apc-v3-shell')) {
    return;
  }

  if (context === 'standalone') {
    throw new Error('Standalone mount is missing #apc-v3-shell markup in app/index.html');
  }

  container.innerHTML = await loadShellMarkup(assetBaseUrl);
}

function resolveMarket(configMarket: MarketplaceCode | undefined, domMarket: string): MarketplaceCode {
  if (configMarket) return configMarket;
  if (domMarket === 'CA' || domMarket === 'UK' || domMarket === 'DE') return domMarket;
  return 'US';
}

function resolveShowHeader(config: BootConfig, context: BootContext): boolean {
  if (typeof config.showHeader === 'boolean') {
    return config.showHeader;
  }

  return context === 'standalone';
}

function replaceHeadingTagsForWordPress(container: HTMLElement): void {
  const headings = Array.from(container.querySelectorAll('h1'));

  headings.forEach((heading) => {
    const replacement = container.ownerDocument.createElement('div');

    Array.from(heading.attributes).forEach((attribute) => {
      replacement.setAttribute(attribute.name, attribute.value);
    });

    while (heading.firstChild) {
      replacement.appendChild(heading.firstChild);
    }

    heading.replaceWith(replacement);
  });
}

function sanitizeWordPressSeoNodes(container: HTMLElement): void {
  const selectors = [
    '#app-title',
    '#app-subtitle',
    '#app-byline',
    '[data-app-subtitle]',
    '[data-app-subheading]',
    '[data-app-byline]'
  ];

  selectors.forEach((selector) => {
    container.querySelectorAll(selector).forEach((node) => node.remove());
  });

  replaceHeadingTagsForWordPress(container);
}

export async function bootstrapInContainer(container: HTMLElement, config: BootConfig = {}): Promise<void> {
  const context: BootContext = config.context ?? 'wordpress';
  const assetBaseUrl = resolveAssetBaseUrl(config);

  container.classList.add('apc-v3-root');
  container.setAttribute('data-apc-context', context);
  container.setAttribute('data-apc-mounted', '0');

  await ensureContainerMarkup(container, context, assetBaseUrl);
  if (context === 'wordpress') {
    sanitizeWordPressSeoNodes(container);
  }

  const dom = getDomRefs(container);
  const profiles = await loadAllProfiles(assetBaseUrl);

  const market = resolveMarket(config.marketplace, dom.marketplace.value);
  const profile = profiles.get(market) || profiles.get('US');
  if (!profile) {
    throw new Error('US profile not available');
  }

  dom.marketplace.value = market;
  setHidden(dom.appTitle, !resolveShowHeader(config, context));

  const store = new Store({
    market,
    profile,
    input: defaultInput(),
    result: null
  });

  attachEvents(store, dom, profiles);
  render(store.getState(), container);
  container.setAttribute('data-apc-mounted', '1');
}

export async function bootstrapStandalone(config: BootConfig = {}): Promise<void> {
  const host = document.getElementById('apc-v3-host');
  if (!host) {
    throw new Error('Missing standalone host #apc-v3-host');
  }

  await bootstrapInContainer(host, {
    ...config,
    context: 'standalone',
    showHeader: typeof config.showHeader === 'boolean' ? config.showHeader : true
  });
}
